import * as React from 'react'
import { Loader2, Mail, UserPlus, X } from 'lucide-react'
import { createInviteInputSchema } from '@lib/input/invite.input'
import type { InviteDto } from '@lib/dto/invite.dto'
import type { UserDto } from '@lib/dto/user.dto'
import { formatDate } from '@lib/helpers/format'
import { useAuth } from '@/auth/AuthContext'
import { useInvites } from '@/hooks/useInvites'
import { useUsers } from '@/hooks/useUsers'
import { useCreateInvite } from '@/hooks/useCreateInvite'
import { useRevokeInvite } from '@/hooks/useRevokeInvite'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { InviteStatusBadge } from '@/components/InviteStatusBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  COL_SECONDARY,
  COL_STICKY,
  ROW,
  ROW_ACTION,
  TABLE,
  TABLE_FRAME,
  TD,
  TH,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'
import { useOrganizationBranding } from '@/hooks/useOrganization'

/** Turn an invite failure into a message that says what to do next. */
function inviteErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    if (error.status === 409) {
      return error.body.message ?? 'That address already has an account.'
    }
    return error.body.message ?? 'Could not send the invite. Please try again.'
  }
  return toErrorMessage(error)
}

/** Only an open invite can be revoked; the rest are already terminal. */
function isRevocable(invite: InviteDto): boolean {
  return invite.status === 'PENDING'
}

interface InviteFormProps {
  onDone: () => void
}

/**
 * The invite form. Follows the design system's list-to-create pattern: the
 * resting state of the page is the list, and this appears only when asked for.
 */
function InviteForm({ onDone }: InviteFormProps) {
  const createInvite = useCreateInvite()
  const [email, setEmail] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)
  const [sentTo, setSentTo] = React.useState<string | null>(null)

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setError(null)
    setSentTo(null)

    const parsed = createInviteInputSchema.safeParse({ email })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a valid email address')
      return
    }

    try {
      const invite = await createInvite.mutateAsync(parsed.data)
      setSentTo(invite.email)
      setEmail('')
    } catch (caught) {
      setError(inviteErrorMessage(caught))
    }
  }

  return (
    <form
      className="rounded-lg border border-default bg-surface p-4"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="invite-email">Email address</Label>
          <Input
            id="invite-email"
            type="email"
            autoComplete="off"
            placeholder="name@smithwellorganics.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={createInvite.isPending}>
            {createInvite.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mail className="h-4 w-4" />
            )}
            Send invite
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      </div>

      {error ? (
        <p className="mt-3 text-sm text-destructive">{error}</p>
      ) : null}
      {sentTo ? (
        <p className="mt-3 text-sm text-secondary-text">
          Invitation sent to <span className="font-medium">{sentTo}</span>. It
          expires in 7 days.
        </p>
      ) : null}
      <p className="mt-3 text-xs text-muted-text">
        Inviting an address that already has an open invite replaces it — the
        older link stops working.
      </p>
    </form>
  )
}

interface MemberRowProps {
  user: UserDto
  isCurrentUser: boolean
}

function MemberRow({ user, isCurrentUser }: MemberRowProps) {
  return (
    <tr className={cn(ROW, 'border-t border-default')}>
      <td className={cn(TD, COL_STICKY)}>
        <span className="font-medium">{user.name}</span>
        {isCurrentUser ? (
          <span className="ml-2 text-xs text-muted-text">You</span>
        ) : null}
      </td>
      <td className={cn(TD, 'text-secondary-text')}>{user.email}</td>
      <td className={cn(TD, COL_SECONDARY, 'text-muted-text')}>
        {formatDate(user.createdAt)}
      </td>
    </tr>
  )
}

interface InviteRowProps {
  invite: InviteDto
  onRevoke: (id: string) => void
  isRevoking: boolean
}

/**
 * One invite row. The revoke action is hidden until the row is hovered or
 * focused — progressive disclosure keeps the resting list free of icon clutter,
 * and keeps a destructive action from sitting under the cursor by default.
 */
function InviteRow({ invite, onRevoke, isRevoking }: InviteRowProps) {
  return (
    <tr className={cn(ROW, 'group border-t border-default')}>
      <td className={cn(TD, COL_STICKY, 'font-medium')}>{invite.email}</td>
      <td className={TD}>
        <InviteStatusBadge status={invite.status} />
      </td>
      <td className={cn(TD, COL_SECONDARY, 'text-muted-text')}>
        {invite.invitedByName ?? '—'}
      </td>
      <td className={cn(TD, COL_SECONDARY, 'text-muted-text')}>
        {formatDate(invite.createdAt)}
      </td>
      <td className={cn(TD, 'text-right')}>
        {isRevocable(invite) ? (
          <Button
            variant="ghost"
            size="sm"
            className={ROW_ACTION}
            disabled={isRevoking}
            onClick={() => onRevoke(invite.id)}
          >
            {isRevoking ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
            Revoke
          </Button>
        ) : null}
      </td>
    </tr>
  )
}

/**
 * Team management: who has access, and who has been invited. This is the only
 * way an account is created — there is no self-signup — so this page is the
 * app's access-control surface.
 */
export function TeamPage() {
  const { user } = useAuth()
  const organization = useOrganizationBranding()
  const organizationName = organization.data?.name ?? 'this workspace'
  const users = useUsers()
  const invites = useInvites()
  const revokeInvite = useRevokeInvite()

  const [isInviting, setIsInviting] = React.useState<boolean>(false)
  const [revokeError, setRevokeError] = React.useState<string | null>(null)
  const [revokingId, setRevokingId] = React.useState<string | null>(null)

  async function handleRevoke(id: string): Promise<void> {
    setRevokeError(null)
    setRevokingId(id)
    try {
      await revokeInvite.mutateAsync(id)
    } catch (caught) {
      setRevokeError(inviteErrorMessage(caught))
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Team</h1>
          <p className="mt-1 text-sm text-muted-text">
            Everyone with access to {organizationName}, and any outstanding
            invitations.
          </p>
        </div>
        {isInviting ? null : (
          <Button onClick={() => setIsInviting(true)}>
            <UserPlus className="h-4 w-4" />
            Invite someone
          </Button>
        )}
      </header>

      {isInviting ? <InviteForm onDone={() => setIsInviting(false)} /> : null}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">
          Members{' '}
          {users.data ? (
            <span className="text-sm font-normal text-muted-text">
              {users.data.total}
            </span>
          ) : null}
        </h2>
        <MembersTable
          isPending={users.isPending}
          isError={users.isError}
          users={users.data?.users ?? []}
          currentUserId={user?.id ?? null}
        />
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Invitations</h2>
        {revokeError ? (
          <p className="text-sm text-destructive">{revokeError}</p>
        ) : null}
        <InvitesTable
          isPending={invites.isPending}
          isError={invites.isError}
          invites={invites.data?.invites ?? []}
          onRevoke={handleRevoke}
          revokingId={revokingId}
        />
      </section>
    </div>
  )
}

interface MembersTableProps {
  isPending: boolean
  isError: boolean
  users: UserDto[]
  currentUserId: string | null
}

function MembersTable({
  isPending,
  isError,
  users,
  currentUserId,
}: MembersTableProps) {
  if (isPending) return <TableSkeleton columns={3} />
  if (isError) {
    return (
      <TableMessage>
        Could not load the member list. Refresh to try again.
      </TableMessage>
    )
  }
  if (users.length === 0) {
    return <TableMessage>No members yet.</TableMessage>
  }

  return (
    <div className={TABLE_FRAME}>
      <table className={TABLE}>
        <thead>
          <tr className="text-secondary-text">
            <th className={cn(TH, COL_STICKY, 'bg-surface')}>Name</th>
            <th className={TH}>Email</th>
            <th className={cn(TH, COL_SECONDARY)}>Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((member) => (
            <MemberRow
              key={member.id}
              user={member}
              isCurrentUser={member.id === currentUserId}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface InvitesTableProps {
  isPending: boolean
  isError: boolean
  invites: InviteDto[]
  onRevoke: (id: string) => void
  revokingId: string | null
}

function InvitesTable({
  isPending,
  isError,
  invites,
  onRevoke,
  revokingId,
}: InvitesTableProps) {
  if (isPending) return <TableSkeleton columns={5} />
  if (isError) {
    return (
      <TableMessage>
        Could not load invitations. Refresh to try again.
      </TableMessage>
    )
  }
  if (invites.length === 0) {
    return (
      <TableMessage>
        No invitations yet. Use “Invite someone” to add a teammate.
      </TableMessage>
    )
  }

  return (
    <div className={TABLE_FRAME}>
      <table className={TABLE}>
        <thead>
          <tr className="text-secondary-text">
            <th className={cn(TH, COL_STICKY, 'bg-surface')}>Email</th>
            <th className={TH}>Status</th>
            <th className={cn(TH, COL_SECONDARY)}>Invited by</th>
            <th className={cn(TH, COL_SECONDARY)}>Sent</th>
            <th className={TH} />
          </tr>
        </thead>
        <tbody>
          {invites.map((invite) => (
            <InviteRow
              key={invite.id}
              invite={invite}
              onRevoke={onRevoke}
              isRevoking={revokingId === invite.id}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface TableSkeletonProps {
  columns: number
}

/** Placeholder rows sized to the real table, so the layout doesn't jump. */
function TableSkeleton({ columns }: TableSkeletonProps) {
  return (
    <div className="rounded-lg border border-default bg-surface">
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          className="flex items-center gap-4 border-t border-default px-3 py-2 first:border-t-0"
        >
          {Array.from({ length: columns }, (_, column) => (
            <div
              key={column}
              className="h-3.5 flex-1 animate-pulse rounded bg-subtle"
            />
          ))}
        </div>
      ))}
    </div>
  )
}

interface TableMessageProps {
  children: React.ReactNode
}

function TableMessage({ children }: TableMessageProps) {
  return (
    <div className="rounded-lg border border-default bg-surface px-3 py-8 text-center text-sm text-muted-text">
      {children}
    </div>
  )
}
